import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Add, Delete, RocketLaunch, ShoppingCart } from '@mui/icons-material';

import Container from '../../components/blocks/container';
import InfoBox from '../../components/blocks/messages/info-box';
import CustomModal from '../../components/blocks/custom-modal';
import CommonButton from '../../components/buttons/common-button';

import PartOrdersService from '../../services/PartOrdersService';

/**
 * PartsOrdersPage Component
 * Displays a flat table of part requests with extra columns,
 * sorted by intervention date then technician,
 * with filters applied via a button (resets selections).
 */
const PartsOrdersPage = () => {
    const navigate = useNavigate();

    /** State variables for managing part requests */
    const [partsRequests, setPartsRequests] = useState([]);
    const [selectedRows, setSelectedRows] = useState({});
    const [invalidRows, setInvalidRows] = useState([]);

    /** State variables for managing messages */
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    /** State variables for managing filter inputs and applied filters */
    const [confirmModal, setConfirmModal] = useState({ open: false, type: null, index: null });

    // raw inputs for filters
    const [inputTech, setInputTech] = useState('');
    const [inputFolder, setInputFolder] = useState('');
    const [inputDate, setInputDate] = useState('');
    // applied filters
    const [filterTech, setFilterTech] = useState('');
    const [filterFolder, setFilterFolder] = useState('');
    const [filterDate, setFilterDate] = useState('');

    // tri dynamique
    const [sortColumn, setSortColumn] = useState('appointment_date');
    const [sortDirection, setSortDirection] = useState('asc');

    /**
     * Fetch all part orders from API
     */
    const fetchData = async () => {
        try {
            const data = await PartOrdersService.getPartOrdersSent();
            setPartsRequests(data);
        } catch (err) {
            console.error('Error fetching data:', err);
            setErrorMessage('Impossible de charger les commandes de pièces.');
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Compute displayed requests based on filters and sorting
     */
    const displayedRequests = useMemo(() => {
        let list = partsRequests
            .filter((r) =>
                r.technician.toLowerCase().includes(filterTech.toLowerCase()) &&
                r.folder_ref.toString().includes(filterFolder) &&
                (filterDate === '' || r.appointment_date === filterDate)
            );

        list = list.sort((a, b) => {
            const aVal = a[sortColumn];
            const bVal = b[sortColumn];

            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return list;
    }, [partsRequests, filterTech, filterFolder, filterDate, sortColumn, sortDirection]);

    /**
     * Apply filter inputs and reset selections
     */
    const handleApplyFilter = () => {
        setFilterTech(inputTech);
        setFilterFolder(inputFolder);
        setFilterDate(inputDate);
        setSelectedRows({});
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
        setSelectedRows({});
    };

    /**
     * Handle input changes in table rows
     */
    const handleChange = (index, field, value) => {
        setErrorMessage(null);
        setSuccessMessage(null);

        setPartsRequests((prev) => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: field === 'quantity' ? parseInt(value, 10) : value,
            };
            return updated;
        });

        setInvalidRows((prev) => prev.filter((i) => i !== index));
    };

    /**
     * Toggle selection checkbox for a row
     */
    const toggleRow = (index) => {
        setSelectedRows((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    /**
     * Validate rows: name, reference, quantity
     */
    const validateRows = (onlySelected) => {
        const invalid = [];
            partsRequests.forEach((row, index) => {
            if (onlySelected && !selectedRows[index]) return;

            const hasName = row.part_name?.trim().length > 0;
            const hasRef = row.part_reference?.trim().length > 0;
            const validQty = typeof row.quantity === 'number' && row.quantity >= 1;

            if (!hasName || !hasRef || !validQty) invalid.push(index);
        });
        setInvalidRows(invalid);
        return invalid;
    };

    /**
     * Handle confirmation modal actions
     * @param {string} type - Type of action ('delete', 'sendAll', 'send')
     */
    const handleConfirmModal = (type, index) => {
        setConfirmModal({ open: false, type: null, index: null });

        if (type === 'delete') {
            handleDelete(index);
        }
        else if (type === 'sendSelected') {
            sendSelected();
        }
        else if (type === 'send') {
            sendOne(index);
        }
        else {
            console.error('Unknown confirm modal type:', type);
        }
    };

    /**
     * Delete a row, with confirmation if last of appointment
     */
    const handleDelete = (index) => {
        const row = partsRequests[index];

        const doDelete = async () => {
            if (row.id) await PartOrdersService.deletePartOrder(row.id);
            setPartsRequests((prev) => prev.filter((_, i) => i !== index));
            setSelectedRows((prev) => {
                const copy = { ...prev };
                delete copy[index];
                return copy;
            });
            setInvalidRows((prev) => prev.filter((i) => i !== index));
        };

        doDelete();
    };

    /**
     * Send only selected requests after validation
     */
    const sendSelected = async () => {
        if (validateRows(true).length) {
            setErrorMessage('Veuillez corriger les erreurs dans les lignes sélectionnées.');
            return;
        }

        try {
            await Promise.all(
                partsRequests.map((r, i) => {
                    if (!selectedRows[i]) return null;

                    const base = {
                        part_name: r.part_name,
                        part_reference: r.part_reference,
                        quantity: r.quantity,
                        state: 'COMMANDE',
                    };

                    return r.id
                        ? PartOrdersService.editPartOrder(r.id, base)
                        : PartOrdersService.createPartOrder({
                            ...base,
                            id_appointment: r.id_appointment,
                        });
                })
                .filter(Boolean)
            );

            await fetchData();

            setSelectedRows({});
            setInvalidRows([]);

            setSuccessMessage('Les pièces sélectionnées ont été enregistrées comme commandées avec succès.');
        } catch (err) {
            console.error('Error sending selected:', err);
            setErrorMessage("Erreur lors de l'envoi des enregistrements de pièces sélectionnées." + err?.message);
        }
    };

    /**
     * Send a single request after validation
     */
    const sendOne = async (index) => {
        if (validateRows(true).length) {
            setErrorMessage('Veuillez corriger les erreurs dans la ligne sélectionnée.');
            return;
        }

        const row = partsRequests[index];
        const base = {
            part_name: row.part_name,
            part_reference: row.part_reference,
            quantity: row.quantity,
            state: 'COMMANDE',
        };

        try {
            if (row.id) {
                await PartOrdersService.editPartOrder(row.id, base);
            } else {
                await PartOrdersService.createPartOrder({
                    ...base,
                    id_appointment: row.id_appointment,
                });
            }
            await fetchData();
            setSelectedRows({});
            setInvalidRows([]);
            setSuccessMessage('La pièce a été enregistrée comme commandée avec succès.');
        } catch (err) {
            setErrorMessage("Erreur lors de l'envoi de l\'enregistrement de la pièce : " + err?.message);
        }
    };

    return (
        <Container
            className="appointment-parts-order parts-requests-page"
            title="Commande de pièces"
            titleBackground="var(--primary-color)"
        >
            {(errorMessage || successMessage) && (
                <InfoBox
                    text={errorMessage || successMessage}
                    type={errorMessage ? 'error' : 'success'}
                    disabledCloseBtn={!errorMessage}
                />
            )}

            {/* Filters */}
            <div className="filters-bar">
                <input
                    type="text"
                    placeholder="Technicien"
                    value={inputTech}
                    onChange={(e) => setInputTech(e.target.value)}
                />
                <input
                    type="text"
                    placeholder="N° dossier"
                    value={inputFolder}
                    onChange={(e) => setInputFolder(e.target.value)}
                />
                <input
                    type="date"
                    placeholder="Date"
                    value={inputDate}
                    onChange={(e) => setInputDate(e.target.value)}
                />
                <CommonButton
                    label="Filtrer"
                    onClick={handleApplyFilter}
                    className="btn-apply-filter"
                    autoWidth={true}
                />
            </div>

            {displayedRequests.length === 0 ? (
                <div className="no-requests-global">Aucune commande de pièces à afficher</div>
            ) : (
                <React.Fragment>
                    <table className="appointment-parts-order-table w-100">
                        <thead>
                            <tr>
                                <th></th>
                                <th onClick={() => handleSort('folder_ref')}>
                                    N° de dossier {sortColumn === 'folder_ref' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('appointment_date')}>
                                    Date d'intervention {sortColumn === 'appointment_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('technician')}>
                                    Technicien {sortColumn === 'technician' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('part_name')}>
                                    Nom {sortColumn === 'part_name' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('part_reference')}>
                                    Référence {sortColumn === 'part_reference' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th onClick={() => handleSort('quantity')}>
                                    Quantité {sortColumn === 'quantity' && (sortDirection === 'asc' ? '↑' : '↓')}
                                </th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedRequests.map((row) => {
                                const globalIndex = partsRequests.indexOf(row);
                                const isChecked = !!selectedRows[globalIndex];
                                const isInvalid = invalidRows.includes(globalIndex);
                                return (
                                    <tr
                                        key={globalIndex}
                                        className={isInvalid ? 'invalid-row' : ''}
                                    >
                                        <td data-label="Sélectionner">
                                            <input
                                                type="checkbox"
                                                checked={isChecked}
                                                onChange={() => toggleRow(globalIndex)}
                                            />
                                        </td>
                                        <td
                                            data-label="N° de dossier"
                                            className='folder-btn'
                                            onClick={() => navigate(`/folders/${row.folder_ref}/overview-only`)}
                                        >
                                            {row.folder_ref}
                                        </td>
                                        <td data-label="Date d'intervention">{row.appointment_date}</td>
                                        <td data-label="Technicien">{row.technician}</td>
                                        <td data-label="Nom de la pièce">
                                            <input
                                                type="text"
                                                value={row.part_name || ''}
                                                onChange={(e) => handleChange(globalIndex, 'part_name', e.target.value)}
                                            />
                                        </td>
                                        <td data-label="Référence">
                                            <input
                                                type="text"
                                                value={row.part_reference || ''}
                                                onChange={(e) => handleChange(globalIndex, 'part_reference', e.target.value)}
                                            />
                                        </td>
                                        <td data-label="Quantité">
                                            <input
                                                type="number"
                                                min="1"
                                                value={row.quantity || 1}
                                                onChange={(e) => handleChange(globalIndex, 'quantity', e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <div className="actions-col">
                                                <CommonButton
                                                    className="btn-remove-row"
                                                    label="Supprimer"
                                                    icon={<Delete />}
                                                    onClick={() => setConfirmModal({ open: true, type: 'delete', index: globalIndex })}
                                                />
                                                <CommonButton
                                                    className="btn-send-row"
                                                    label={<div>Passer la pièce à l'état <strong>Commandé</strong></div>}
                                                    icon={<ShoppingCart />}
                                                    onClick={() => setConfirmModal({ open: true, type: 'send', index: globalIndex })}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>

                    <div className="actions-bar">
                        <CommonButton
                            label={<div>Passer toutes les pièces sélectionnées à l'état <strong>Commandé</strong></div>}
                            icon={<RocketLaunch />}
                            onClick={() => setConfirmModal({ open: true, type: 'sendSelected', index: null })}
                            className="btn-send-selected"
                        />
                    </div>

                    {confirmModal.open && (
                        <CustomModal
                            open
                            title={confirmModal.type === 'delete' ? 'Confirmer la suppression' : 'Confirmer l\'envoi'}
                            handleSave={() => handleConfirmModal(confirmModal.type, confirmModal.index)}
                            onClose={() => setConfirmModal({ open: false, type: null, index: null })}
                        >
                            {confirmModal.type === 'delete' ? (
                                <InfoBox
                                    text="Êtes-vous sûr de vouloir supprimer cette ligne ? Cette action est irréversible."
                                    type="error"
                                    disabledCloseBtn={true}
                                />
                            ) : confirmModal.type === 'sendAll' ? (
                                <InfoBox
                                    text="Êtes-vous sûr de vouloir passer toutes les pièces sélectionnées à l'état Commandé ? Cette action est irréversible."
                                    type="warning"
                                    disabledCloseBtn={true}
                                />
                            ) : confirmModal.type === 'send' ? (
                                <InfoBox
                                    text="Êtes-vous sûr de vouloir passer cette pièce à l'état Commandé ? Cette action est irréversible."
                                    type="warning"
                                    disabledCloseBtn={true}
                                />
                            ) : (
                                <InfoBox
                                    text="Êtes-vous sûr de vouloir supprimer cette ligne ?"
                                    type="warning"
                                    disabledCloseBtn={true}
                                />
                            )}
                        </CustomModal>
                    )}
                </React.Fragment>
            )}
        </Container>
    );
};

export default PartsOrdersPage;